import type { MarkupShape } from './markupShapes.ts';

type Snapshot = readonly MarkupShape[];

export interface MarkupState {
    shapes: Snapshot;
    past: readonly Snapshot[];
    future: readonly Snapshot[];
}

export type MarkupAction =
    | { type: 'add'; shape: MarkupShape }
    | { type: 'update'; shape: MarkupShape }
    | { type: 'remove'; id: string }
    | { type: 'clear' }
    | { type: 'undo' }
    | { type: 'redo' }
    | { type: 'reset' };

export const INITIAL_MARKUP_STATE: MarkupState = { shapes: [], past: [], future: [] };

// A no-op edit returns the same state, so it neither records a step nor drops the redo stack.
function commit(state: MarkupState, shapes: Snapshot): MarkupState {
    if (shapes === state.shapes) return state;
    return { shapes, past: [...state.past, state.shapes], future: [] };
}

function replaceShape(shapes: Snapshot, next: MarkupShape): Snapshot {
    const index = shapes.findIndex((shape) => shape.id === next.id);
    if (index < 0 || shapes[index] === next) return shapes;
    return shapes.map((shape, i) => (i === index ? next : shape));
}

function removeShape(shapes: Snapshot, id: string): Snapshot {
    return shapes.some((shape) => shape.id === id)
        ? shapes.filter((shape) => shape.id !== id)
        : shapes;
}

function undo(state: MarkupState): MarkupState {
    const previous = state.past[state.past.length - 1];
    if (!previous) return state;
    return {
        shapes: previous,
        past: state.past.slice(0, -1),
        future: [state.shapes, ...state.future]
    };
}

function redo(state: MarkupState): MarkupState {
    const [next, ...rest] = state.future;
    if (!next) return state;
    return { shapes: next, past: [...state.past, state.shapes], future: rest };
}

export function markupReducer(state: MarkupState, action: MarkupAction): MarkupState {
    switch (action.type) {
        case 'add':
            return commit(state, [...state.shapes, action.shape]);
        case 'update':
            return commit(state, replaceShape(state.shapes, action.shape));
        case 'remove':
            return commit(state, removeShape(state.shapes, action.id));
        case 'clear':
            return state.shapes.length === 0 ? state : commit(state, []);
        case 'undo':
            return undo(state);
        case 'redo':
            return redo(state);
        case 'reset':
            return INITIAL_MARKUP_STATE;
    }
}

export function canUndo(state: MarkupState): boolean {
    return state.past.length > 0;
}

export function canRedo(state: MarkupState): boolean {
    return state.future.length > 0;
}

export function hasMarks(state: MarkupState): boolean {
    return state.shapes.length > 0;
}
